/**
 * CSV Import API Client (Refactored)
 * Main orchestration layer that coordinates all CSV import operations
 * Uses modular components for better maintainability
 */

import BaseApiClient from './api/BaseApiClient';
import ProfileManager from './profiles/ProfileManager';
import PriceCalculator from './pricing/PriceCalculator';
import OrderBuilder from './orders/OrderBuilder';
import CountryShippingUtils from './shipping/CountryShippingUtils';
import CSVProcessingUtils from './csv/CSVProcessingUtils';

// Debug logging
const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';

class CSVImportApiClient {
    constructor() {
        // Initialize all modules
        this.baseClient = new BaseApiClient();
        this.profileManager = new ProfileManager(this.baseClient);
        this.priceCalculator = new PriceCalculator(this.baseClient);
        this.countryShipping = new CountryShippingUtils(this.baseClient);
        this.csvProcessor = new CSVProcessingUtils();
        
        // Initialize OrderBuilder with proper dependency injection
        this.orderBuilder = new OrderBuilder(this.baseClient, this.priceCalculator, this.countryShipping);
    }

    // ===== Profile Management Methods =====
    async getProfiles() {
        console.log('[CSVImportApiClient] getProfiles called');
        const result = await this.profileManager.getProfiles();
        console.log('[CSVImportApiClient] getProfiles result:', result);
        return result;
    }

    async getImportProfiles() {
        return this.profileManager.getImportProfiles();
    }

    async createImportProfile(profileData) {
        return this.profileManager.createImportProfile(profileData);
    }

    async updateImportProfile(profileId, profileData) {
        return this.profileManager.updateImportProfile(profileId, profileData);
    }

    async deleteImportProfile(profileId) {
        return this.profileManager.deleteImportProfile(profileId);
    }

    async copyImportProfile(sourceProfileId, newProfileName) {
        return this.profileManager.copyImportProfile(sourceProfileId, newProfileName);
    }

    // ===== Customer Management Methods =====
    async getCustomers(searchTerm, limit = 25) {
        return this.baseClient.getCustomers(searchTerm, limit);
    }

    async getCustomerDetails(customerId) {
        return this.baseClient.getCustomerDetails(customerId);
    }

    async getCustomerAddresses(customerId) {
        return this.baseClient.getCustomerAddresses(customerId);
    }

    // ===== System Data Methods =====
    async getSalesChannels() {
        return this.baseClient.getSalesChannels();
    }

    async getPaymentMethods() {
        return this.baseClient.getPaymentMethods();
    }

    async getShippingMethods() {
        return this.baseClient.getShippingMethods();
    }

    async getCountries() {
        return this.baseClient.getCountries();
    }

    async findProductsByNumbers(productNumbers) {
        try {
            const products = await this.baseClient.findProductsByNumbers(productNumbers);
            return {
                success: true,
                data: {
                    products: products || []
                },
                total: products ? products.length : 0
            };
        } catch (error) {
            console.error('Error finding products:', error);
            return {
                success: false,
                data: { products: [] },
                total: 0,
                message: error.message
            };
        }
    }

    async createOrder(orderPayload) {
        return this.baseClient.createOrder(orderPayload);
    }

    // ===== CSV Processing Methods =====
    
    /**
     * Parse CSV file and extract headers
     * @param {File} file - CSV file object
     * @returns {Object} Parse result with headers
     */
    async parseCSVHeader(file) {
        try {
            const csvData = await this.readCSVFile(file);
            
            // csvData already contains headers directly from readCSVFile
            if (csvData.headers && csvData.headers.length > 0) {
                // Add delimiter info to success response
                const result = {
                    success: true,
                    data: {
                        headers: csvData.headers
                    },
                    headers: csvData.headers, // For backward compatibility
                    delimiterInfo: csvData.delimiterInfo
                };
                
                // Log delimiter info for user feedback
                if (csvData.delimiterInfo && csvData.delimiterInfo.confidence < 90) {
                    console.warn('[CSV Parser] Trennzeichen-Warnung:', csvData.delimiterInfo.message);
                }
                
                return result;
            } else {
                return {
                    success: false,
                    errors: ['CSV-Datei hat keine erkennbaren Spaltenüberschriften'],
                    headers: []
                };
            }
        } catch (error) {
            return {
                success: false,
                errors: [error.message],
                headers: []
            };
        }
    }

    /**
     * Detect CSV delimiter from file content
     * @param {string} csvContent - Raw CSV content
     * @returns {Object} Delimiter detection result
     */
    detectCSVDelimiter(csvContent) {
        const lines = csvContent.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
            return { delimiter: ';', confidence: 0, message: 'Keine Daten gefunden' };
        }
        
        const firstLine = lines[0];
        const delimiters = [
            { char: ';', name: 'Semikolon (;)', type: 'Deutsch/Europäisch' },
            { char: ',', name: 'Komma (,)', type: 'US/International' },
            { char: '\t', name: 'Tab (\t)', type: 'Tab-separiert' },
            { char: '|', name: 'Pipe (|)', type: 'Pipe-separiert' }
        ];
        
        const results = delimiters.map(del => {
            const count = (firstLine.match(new RegExp(del.char === '\t' ? '\t' : `\\${del.char}`, 'g')) || []).length;
            return { ...del, count };
        });
        
        // Sort by count (descending)
        results.sort((a, b) => b.count - a.count);
        
        const bestMatch = results[0];
        const secondBest = results[1];
        
        // Calculate confidence
        let confidence = 0;
        let message = '';
        
        if (bestMatch.count === 0) {
            confidence = 0;
            message = 'Keine bekannten Trennzeichen gefunden. Möglicherweise einspaltiges Format oder unbekanntes Trennzeichen.';
        } else if (bestMatch.count === 1) {
            confidence = 50;
            message = `Wenige Trennzeichen gefunden. Möglicherweise nur eine Datenzeile.`;
        } else if (secondBest.count === 0 || bestMatch.count > secondBest.count * 2) {
            confidence = 90;
            message = `Eindeutiges Trennzeichen erkannt: ${bestMatch.name} (${bestMatch.type})`;
        } else {
            confidence = 70;
            message = `Wahrscheinliches Trennzeichen: ${bestMatch.name} (${bestMatch.type}), aber ${secondBest.name} ist auch möglich.`;
        }
        
        return {
            delimiter: bestMatch.char,
            confidence,
            message,
            alternatives: results.filter(r => r.count > 0 && r.char !== bestMatch.char)
        };
    }

    /**
     * Read CSV file and parse content
     * @param {File} file - CSV file object
     * @returns {Object} Parsed CSV data
     */
    readCSVFile(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csv = e.target.result;
                    const lines = csv.split('\n').filter(line => line.trim());
                    
                    if (lines.length <= 1) {
                        resolve({ data: [], headers: [], delimiterInfo: null });
                        return;
                    }
                    
                    // Detect delimiter
                    const delimiterInfo = this.detectCSVDelimiter(csv);
                    console.log('[CSV Parser] Delimiter detection:', delimiterInfo);
                    
                    // Parse with detected delimiter
                    const delimiter = delimiterInfo.delimiter;
                    const headers = lines[0].split(delimiter).map(h => h.trim().replace(/"/g, ''));
                    
                    // Validate headers
                    if (headers.length <= 1) {
                        // If we only got one header, the delimiter might be wrong
                        const errorMessage = `CSV-Trennzeichen-Problem erkannt!\n\n` +
                                          `Erkanntes Trennzeichen: ${delimiterInfo.message}\n\n` +
                                          `Gefundene Spalten: ${headers.length} ("${headers[0]}")\n\n` +
                                          `Mögliche Ursachen:\n` +
                                          `• Falsche Datei-Kodierung (sollte UTF-8 sein)\n` +
                                          `• Ungewöhnliches Trennzeichen verwendet\n` +
                                          `• Datei ist nicht im CSV-Format\n\n` +
                                          `Erste Zeile der Datei: "${lines[0].substring(0, 100)}${lines[0].length > 100 ? '...' : ''}"`;
                        
                        reject(new Error(errorMessage));
                        return;
                    }
                    
                    // Parse data rows
                    const data = lines.slice(1).map(line => {
                        const values = line.split(delimiter).map(v => v.trim().replace(/"/g, ''));
                        const row = {};
                        headers.forEach((header, index) => {
                            row[header] = values[index] || '';
                        });
                        return row;
                    });
                    
                    console.log(`[CSV Parser] Successfully parsed ${headers.length} columns, ${data.length} rows`);
                    
                    resolve({ 
                        data, 
                        headers,
                        delimiterInfo: {
                            delimiter,
                            confidence: delimiterInfo.confidence,
                            message: delimiterInfo.message
                        }
                    });
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file, 'UTF-8');
        });
    }

    validateCSVData(csvData, profile) {
        return this.csvProcessor.validateCSVData(csvData, profile);
    }

    performPlausibilityCheck(csvData, profile, customer, salesChannelId) {
        return this.csvProcessor.performPlausibilityCheck(csvData, profile, customer, salesChannelId);
    }

    validateOrderDates(csvData, profile) {
        return this.csvProcessor.validateOrderDates(csvData, profile);
    }

    parseOrderDate(dateValue) {
        return this.csvProcessor.parseOrderDate(dateValue);
    }

    parseCSVData(csvData, profile) {
        return this.csvProcessor.parseCSVData(csvData, profile);
    }

    // ===== Main Import Methods =====
    async importCSV(csvFileOrData, customer, billingAddress, shippingAddress, profile, salesChannelId) {
        try {
            if (DEBUG_API) {
                console.log('[CSV Import] Starting import process...');
                console.log('[CSV Import] Customer:', customer?.customerNumber);
                console.log('[CSV Import] Import type:', profile?.importType);
                console.log('[CSV Import] Sales channel:', salesChannelId);
            }

            // Handle both File objects and parsed data
            let csvData;
            if (csvFileOrData instanceof File) {
                csvData = await this.readCSVFile(csvFileOrData);
            } else {
                csvData = csvFileOrData;
            }

            // Process CSV data
            const csvProductData = this.parseCSVData(csvData, profile);
            
            if (csvProductData.length === 0) {
                throw new Error('Keine gültigen Produktdaten in CSV gefunden');
            }

            // Create orders based on import type
            switch (profile.importType) {
                case 'singleCustomerMultipleOrders':
                case 'single_customer_multiple_orders':
                    return await this.createOrdersFromCSV(csvData, customer, billingAddress, shippingAddress, profile, salesChannelId);
                
                case 'singleCustomerSingleOrderWithPrices':
                case 'single_customer_single_order_with_prices':
                case 'singleCustomerSingleOrderWithoutPrices':
                case 'single_customer_single_order_without_prices':
                case 'single_customer_single_order':
                    return await this.createSingleConsolidatedOrder(csvData, customer, billingAddress, shippingAddress, profile, salesChannelId);
                
                case 'multiCustomerMultipleOrders':
                case 'multi_customer_multiple_orders':
                    return await this.createOrdersFromCSVMultiCustomer(csvData, profile, salesChannelId);
                
                default:
                    throw new Error(`Unbekannter Import-Typ: ${profile.importType}`);
            }

        } catch (error) {
            console.error('Error importing CSV:', error);
            throw error;
        }
    }

    /**
     * Create multiple orders from CSV (grouped by order number, consolidated products)
     */
    async createOrdersFromCSV(csvData, customer, billingAddress, shippingAddress, profile, salesChannelId) {
        const results = {
            success: true,
            created: 0,
            failed: 0,
            orders: [],
            errors: []
        };

        try {
            // Group CSV rows by order number from CSV data
            const groupedOrders = this.csvProcessor.groupCSVRowsByOrderNumber(csvData, profile);
            
            if (DEBUG_API) {
                console.log('[CSV Import] Grouped Orders:', Object.keys(groupedOrders));
                console.log('[CSV Import] Creating separate orders per order number');
            }

            // Get all products in bulk first
            const allProductNumbers = [];
            for (const [, productsInOrder] of Object.entries(groupedOrders)) {
                for (const productNumber of Object.keys(productsInOrder)) {
                    if (!allProductNumbers.includes(productNumber)) {
                        allProductNumbers.push(productNumber);
                    }
                }
            }
            
            if (DEBUG_API) {
                console.log(`[CSV Import] Validating ${allProductNumbers.length} unique products before import`);
            }
            
            // CRITICAL: Validate all products exist before starting import
            const foundProducts = await this.baseClient.findProductsByNumbers(allProductNumbers);
            const foundProductNumbers = foundProducts.map(p => p.productNumber);
            const missingProducts = allProductNumbers.filter(pn => !foundProductNumbers.includes(pn));
            
            if (missingProducts.length > 0) {
                const errorMessage = `Import abgebrochen: ${missingProducts.length} Produkte nicht im System gefunden: ${missingProducts.join(', ')}`;
                console.error(`[CSV Import] ${errorMessage}`);
                
                return {
                    success: false,
                    data: {
                        orderNumbers: [],
                        orderIds: [],
                        orders: [],
                        ordersCreated: 0,
                        ordersFailed: 0,
                        errors: [errorMessage]
                    }
                };
            }
            
            if (DEBUG_API) {
                console.log(`[CSV Import] ✅ All ${allProductNumbers.length} products found in system`);
            }
            
            // Get prices if needed
            if (this.priceCalculator.shouldFetchPrices(profile)) {
                await this.priceCalculator.getProductPricesForProfile(allProductNumbers, profile, customer);
            }

            // Create order for each unique order number
            let orderIndex = 0;
            for (const [orderNumber, productsInOrder] of Object.entries(groupedOrders)) {
                orderIndex++;
                
                try {
                    // Consolidate products for this order
                    const consolidatedCSVData = this.csvProcessor.consolidateOrderProducts(
                        productsInOrder, 
                        orderNumber
                    );
                    
                    if (DEBUG_API) {
                        console.log(`[CSV Import] Order ${orderNumber} - Original products: ${Object.keys(productsInOrder).length}, Consolidated rows: ${consolidatedCSVData.data.length}`);
                    }

                    // Extract shipping address from first CSV row for this order
                    let orderShippingAddress = shippingAddress; // Default to profile address
                    let orderShippingMethodId = profile.defaultValues?.shippingMethodId;
                    
                    if (consolidatedCSVData.data.length > 0) {
                        const firstRowOfOrder = consolidatedCSVData.data[0];
                        const csvShippingAddr = this.csvProcessor.extractShippingAddressFromRow(firstRowOfOrder, profile);
                        
                        if (csvShippingAddr && Object.keys(csvShippingAddr).length > 0) {
                            // Convert country name to country ID if needed
                            if (csvShippingAddr.countryName && !csvShippingAddr.countryId) {
                                const countryId = await this.countryShipping.getCountryIdByName(csvShippingAddr.countryName);
                                if (countryId) {
                                    csvShippingAddr.countryId = countryId;
                                } else {
                                    // Default to Germany if country not found
                                    csvShippingAddr.countryId = '018ad12f185c7185851e2fdd7aa8206d';
                                    if (DEBUG_API) {
                                        console.warn(`[CSV Import] Unknown country "${csvShippingAddr.countryName}", defaulting to Germany`);
                                    }
                                }
                            }

                            // Use CSV shipping address, normalized with profile defaults
                            orderShippingAddress = this.csvProcessor.normalizeShippingAddress(csvShippingAddr, shippingAddress);
                            
                            // Determine country-specific shipping method based on CSV shipping address
                            if (csvShippingAddr.country) {
                                const countryConfig = await this.countryShipping.getCountryTaxShipping(csvShippingAddr.country, profile);
                                if (countryConfig.shippingMethodId) {
                                    orderShippingMethodId = countryConfig.shippingMethodId;
                                    
                                    if (DEBUG_API) {
                                        console.log(`[CSV Import] Using country-specific shipping method for ${csvShippingAddr.country}: ${orderShippingMethodId}`);
                                    }
                                }
                            }
                            
                            if (DEBUG_API) {
                                console.log(`[CSV Import] Using CSV shipping address for order ${orderNumber}:`, orderShippingAddress);
                            }
                        }
                    }

                    // Build order payload with consolidated data
                    const orderPayload = await this.orderBuilder.buildOrderPayload({
                        csvData: consolidatedCSVData,
                        customer,
                        billingAddress,
                        shippingAddress: orderShippingAddress,
                        profile,
                        salesChannelId,
                        orderType: 'multiple',
                        orderIndex: orderIndex,
                        orderNumber: orderNumber,
                        shippingMethodId: orderShippingMethodId
                    });
                    
                    const fallbackPriceProducts = orderPayload.fallbackPriceProducts || [];

                    // Create order
                    const orderResult = await this.baseClient.createOrder(orderPayload);
                    
                    if (orderResult.success) {
                        results.created++;
                        
                        // Handle 204 No Content response (order created but no data returned)
                        if (orderResult.data) {
                            results.orders.push({
                                ...orderResult.data,
                                fallbackPriceProducts: fallbackPriceProducts || []
                            });
                        } else {
                            // Create placeholder order data for UI
                            results.orders.push({
                                id: `created-${Date.now()}-${orderIndex}`,
                                orderNumber: orderPayload.orderNumber || orderNumber,
                                orderDateTime: orderPayload.orderDateTime,
                                fallbackPriceProducts: fallbackPriceProducts || []
                            });
                        }
                        
                        if (DEBUG_API) {
                            console.log(`[CSV Import] ✅ Order ${orderNumber} created successfully`);
                        }
                    } else {
                        results.failed++;
                        results.errors.push(`Order ${orderNumber}: ${orderResult.error}`);
                        
                        console.error(`[CSV Import] ❌ Order ${orderNumber} failed:`, orderResult.error);
                    }

                } catch (orderError) {
                    results.failed++;
                    results.errors.push(`Order ${orderNumber}: ${orderError.message}`);
                    console.error(`[CSV Import] ❌ Order ${orderNumber} exception:`, orderError);
                }
            }

            results.success = results.created > 0;
            
            if (DEBUG_API) {
                console.log(`[CSV Import] Import completed: ${results.created} created, ${results.failed} failed`);
            }

            // Transform response to match UI expectations
            return {
                success: results.success,
                data: {
                    orderNumbers: results.orders.map(order => order.orderNumber || `Order-${order.id}`),
                    orderIds: results.orders.map(order => order.id),
                    orders: results.orders,
                    ordersCreated: results.created,
                    ordersFailed: results.failed,
                    errors: results.errors
                }
            };

        } catch (error) {
            console.error('[CSV Import] Bulk import failed:', error);
            return {
                success: false,
                data: {
                    orderNumbers: [],
                    orderIds: [],
                    orders: [],
                    ordersCreated: 0,
                    ordersFailed: 0,
                    errors: [error.message]
                }
            };
        }
    }

    /**
     * Create single consolidated order from all CSV rows (with product consolidation)
     */
    async createSingleConsolidatedOrder(csvData, customer, billingAddress, shippingAddress, profile, salesChannelId) {
        try {
            if (DEBUG_API) {
                console.log('[CSV Import] Creating single consolidated order...');
            }

            // Consolidate products for single order
            const singleOrderProducts = {};
            const allProductNumbers = [];
            
            // Group all CSV rows by product number
            for (let rowIndex = 0; rowIndex < csvData.data.length; rowIndex++) {
                const row = csvData.data[rowIndex];
                const productNumber = this.csvProcessor.extractProductNumberFromRow(row, profile);
                
                if (productNumber) {
                    if (!singleOrderProducts[productNumber]) {
                        singleOrderProducts[productNumber] = [];
                        allProductNumbers.push(productNumber);
                    }
                    
                    singleOrderProducts[productNumber].push({
                        rowIndex: rowIndex,
                        row: row,
                        quantity: this.csvProcessor.extractQuantityFromRow(row, profile),
                        price: this.csvProcessor.extractPriceFromRow(row, profile),
                        productNumber: productNumber
                    });
                }
            }
            
            if (DEBUG_API) {
                console.log(`[CSV Import] Validating ${allProductNumbers.length} unique products before single order creation`);
            }
            
            // CRITICAL: Validate all products exist before starting import
            const foundProducts = await this.baseClient.findProductsByNumbers(allProductNumbers);
            const foundProductNumbers = foundProducts.map(p => p.productNumber);
            const missingProducts = allProductNumbers.filter(pn => !foundProductNumbers.includes(pn));
            
            if (missingProducts.length > 0) {
                const errorMessage = `Import abgebrochen: ${missingProducts.length} Produkte nicht im System gefunden: ${missingProducts.join(', ')}`;
                console.error(`[CSV Import] ${errorMessage}`);
                
                return {
                    success: false,
                    data: {
                        orderNumbers: [],
                        orderIds: [],
                        orders: [],
                        ordersCreated: 0,
                        ordersFailed: 1,
                        errors: [errorMessage]
                    }
                };
            }
            
            if (DEBUG_API) {
                console.log(`[CSV Import] ✅ All ${allProductNumbers.length} products found in system`);
            }
            
            // Consolidate products for single order
            const consolidatedCSVData = this.csvProcessor.consolidateOrderProducts(
                singleOrderProducts, 
                'SINGLE_ORDER'
            );
            
            if (DEBUG_API) {
                console.log(`[CSV Import] Single order consolidated: ${Object.keys(singleOrderProducts).length} unique products, ${consolidatedCSVData.data.length} final rows`);
            }

            // Build consolidated order payload
            const orderPayload = await this.orderBuilder.buildOrderPayload({
                csvData: consolidatedCSVData,
                customer,
                billingAddress,
                shippingAddress,
                profile,
                salesChannelId,
                orderType: 'single'
            });
            
            const fallbackPriceProducts = orderPayload.fallbackPriceProducts || [];

            // Create order
            const orderResult = await this.baseClient.createOrder(orderPayload);
            
            if (orderResult.success) {
                const order = orderResult.data;
                
                // Handle 204 No Content response (order created but no data returned)
                if (!order) {
                    return {
                        success: true,
                        data: {
                            orderNumbers: [orderPayload.orderNumber || `Order-${Date.now()}`],
                            orderIds: [`created-${Date.now()}`],
                            orders: [{
                                id: `created-${Date.now()}`,
                                orderNumber: orderPayload.orderNumber || `Order-${Date.now()}`,
                                orderDateTime: orderPayload.orderDateTime,
                                fallbackPriceProducts: fallbackPriceProducts
                            }],
                            ordersCreated: 1,
                            ordersFailed: 0,
                            errors: []
                        }
                    };
                }
                
                return {
                    success: true,
                    data: {
                        orderNumbers: [order.orderNumber || `Order-${order.id}`],
                        orderIds: [order.id],
                        orders: [{
                            ...order,
                            fallbackPriceProducts: fallbackPriceProducts
                        }],
                        ordersCreated: 1,
                        ordersFailed: 0,
                        errors: []
                    }
                };
            } else {
                return {
                    success: false,
                    data: {
                        orderNumbers: [],
                        orderIds: [],
                        orders: [],
                        ordersCreated: 0,
                        ordersFailed: 1,
                        errors: [orderResult.error]
                    }
                };
            }

        } catch (error) {
            console.error('[CSV Import] Single order creation failed:', error);
            return {
                success: false,
                data: {
                    orderNumbers: [],
                    orderIds: [],
                    orders: [],
                    ordersCreated: 0,
                    ordersFailed: 1,
                    errors: [error.message]
                }
            };
        }
    }

    /**
     * Create orders from CSV with multiple customers (with order and product consolidation)
     */
    async createOrdersFromCSVMultiCustomer(csvData, profile, salesChannelId) {
        const results = {
            success: true,
            created: 0,
            failed: 0,
            orders: [],
            errors: []
        };

        try {
            // First, validate all products exist before starting import
            const csvProductData = this.csvProcessor.parseCSVData(csvData, profile);
            const allProductNumbers = [...new Set(csvProductData.map(item => item.productNumber))];
            
            if (DEBUG_API) {
                console.log(`[CSV Import] Validating ${allProductNumbers.length} unique products before multi-customer import`);
            }
            
            // CRITICAL: Validate all products exist before starting import
            const foundProducts = await this.baseClient.findProductsByNumbers(allProductNumbers);
            const foundProductNumbers = foundProducts.map(p => p.productNumber);
            const missingProducts = allProductNumbers.filter(pn => !foundProductNumbers.includes(pn));
            
            if (missingProducts.length > 0) {
                const errorMessage = `Import abgebrochen: ${missingProducts.length} Produkte nicht im System gefunden: ${missingProducts.join(', ')}`;
                console.error(`[CSV Import] ${errorMessage}`);
                
                return {
                    success: false,
                    data: {
                        orderNumbers: [],
                        orderIds: [],
                        orders: [],
                        ordersCreated: 0,
                        ordersFailed: 0,
                        errors: [errorMessage]
                    }
                };
            }
            
            if (DEBUG_API) {
                console.log(`[CSV Import] ✅ All ${allProductNumbers.length} products found in system`);
            }
            
            // Group by customer (extracted from CSV data)
            const customerGroups = this.groupCSVRowsByCustomer(csvData, profile);
            
            if (DEBUG_API) {
                console.log('[CSV Import] Multi-customer import:', Object.keys(customerGroups).length, 'customers');
            }

            // Process each customer group
            for (const [customerKey, customerOrders] of Object.entries(customerGroups)) {
                try {
                    // Extract customer data from first row
                    const customerData = await this.extractCustomerFromCSV(customerOrders.firstRow, profile);
                    
                    if (!customerData.customer) {
                        console.warn(`[CSV Import] Customer not found for key: ${customerKey}, skipping orders`);
                        continue;
                    }

                    // Process each order for this customer
                    for (const [orderNumber, productsInOrder] of Object.entries(customerOrders.orders)) {
                        try {
                            // Consolidate products with same product number in this order
                            const consolidatedCSVData = this.csvProcessor.consolidateOrderProducts(
                                productsInOrder, 
                                orderNumber
                            );
                            
                            if (DEBUG_API) {
                                console.log(`[CSV Import] Customer ${customerKey}, Order ${orderNumber} consolidated: ${Object.keys(productsInOrder).length} products`);
                            }

                            // Build order payload
                            const orderPayload = await this.orderBuilder.buildOrderPayload({
                                csvData: consolidatedCSVData,
                                customer: customerData.customer,
                                billingAddress: customerData.billingAddress,
                                shippingAddress: customerData.shippingAddress,
                                profile,
                                salesChannelId,
                                orderType: 'multiple',
                                orderNumber: orderNumber
                            });
                            
                            const fallbackPriceProducts = orderPayload.fallbackPriceProducts || [];

                            // Create order
                            const orderResult = await this.baseClient.createOrder(orderPayload);

                            if (orderResult.success) {
                                results.created++;

                                if (orderResult.data) {
                                    results.orders.push({
                                        ...orderResult.data,
                                        fallbackPriceProducts: fallbackPriceProducts
                                    });
                                } else {
                                    results.orders.push({
                                        id: `created-${Date.now()}-${customerKey}-${orderNumber}`,
                                        orderNumber: orderPayload.orderNumber || orderNumber,
                                        orderDateTime: orderPayload.orderDateTime,
                                        fallbackPriceProducts: fallbackPriceProducts
                                    });
                                }
                                
                                if (DEBUG_API) {
                                    console.log(`[CSV Import] ✅ Customer ${customerKey}, Order ${orderNumber} created successfully`);
                                }
                            } else {
                                results.failed++;
                                results.errors.push(`Customer ${customerKey}, Order ${orderNumber}: ${orderResult.error}`);
                                console.error(`[CSV Import] ❌ Customer ${customerKey}, Order ${orderNumber} failed:`, orderResult.error);
                            }

                        } catch (orderError) {
                            results.failed++;
                            results.errors.push(`Customer ${customerKey}, Order ${orderNumber}: ${orderError.message}`);
                            console.error(`[CSV Import] ❌ Customer ${customerKey}, Order ${orderNumber} exception:`, orderError);
                        }
                    }

                } catch (customerError) {
                    results.failed++;
                    results.errors.push(`Customer ${customerKey}: ${customerError.message}`);
                    console.error(`[CSV Import] ❌ Customer ${customerKey} error:`, customerError);
                }
            }

            results.success = results.created > 0;
            
            if (DEBUG_API) {
                console.log(`[CSV Import] Multi-customer import completed: ${results.created} created, ${results.failed} failed`);
            }

            return {
                success: results.success,
                data: {
                    orderNumbers: results.orders.map(order => order.orderNumber || `Order-${order.id}`),
                    orderIds: results.orders.map(order => order.id),
                    orders: results.orders,
                    ordersCreated: results.created,
                    ordersFailed: results.failed,
                    errors: results.errors
                }
            };

        } catch (error) {
            console.error('[CSV Import] Multi-customer import failed:', error);
            return {
                success: false,
                data: {
                    orderNumbers: [],
                    orderIds: [],
                    orders: [],
                    ordersCreated: 0,
                    ordersFailed: 0,
                    errors: [error.message]
                }
            };
        }
    }

    /**
     * Group CSV rows by customer (extracted from CSV data)
     */
    groupCSVRowsByCustomer(csvData, profile) {
        const customerGroups = {};
        
        for (let rowIndex = 0; rowIndex < csvData.data.length; rowIndex++) {
            const row = csvData.data[rowIndex];
            
            // Extract customer key from CSV row
            const customerKey = this.extractCustomerKeyFromRow(row, profile);
            
            // Extract order number from CSV row
            const orderNumber = this.csvProcessor.extractOrderNumberFromRow(row, profile, rowIndex);
            
            // Extract product number from CSV row
            const productNumber = this.csvProcessor.extractProductNumberFromRow(row, profile);
            
            if (!productNumber) {
                console.warn(`[CSV Processing] Skipping row ${rowIndex + 1} - no product number found`);
                continue;
            }

            // Initialize customer group if not exists
            if (!customerGroups[customerKey]) {
                customerGroups[customerKey] = {
                    firstRow: row,
                    orders: {}
                };
            }

            // Initialize order group if not exists
            if (!customerGroups[customerKey].orders[orderNumber]) {
                customerGroups[customerKey].orders[orderNumber] = {};
            }

            // Initialize product group if not exists
            if (!customerGroups[customerKey].orders[orderNumber][productNumber]) {
                customerGroups[customerKey].orders[orderNumber][productNumber] = [];
            }

            // Add row data to product group
            customerGroups[customerKey].orders[orderNumber][productNumber].push({
                rowIndex: rowIndex,
                row: row,
                quantity: this.csvProcessor.extractQuantityFromRow(row, profile),
                price: this.csvProcessor.extractPriceFromRow(row, profile),
                productNumber: productNumber
            });
        }

        return customerGroups;
    }

    /**
     * Extract customer key from CSV row
     */
    extractCustomerKeyFromRow(row, profile) {
        const fieldMapping = profile.fieldMapping || {};
        
        // Find customer identifier mapping
        const customerFields = ['customerNumber', 'customerEmail', 'customerName', 'email'];
        
        for (const field of customerFields) {
            const mappingKey = Object.keys(fieldMapping).find(key => 
                fieldMapping[key].includes(field) || 
                key.toLowerCase().includes(field.toLowerCase())
            );
            
            if (mappingKey && row[mappingKey]) {
                return row[mappingKey].toString().trim();
            }
        }
        
        // Fallback: use first available field that looks like customer data
        for (const [key, value] of Object.entries(row)) {
            if (key.toLowerCase().includes('customer') || key.toLowerCase().includes('kunde')) {
                return value.toString().trim();
            }
        }
        
        return 'UNKNOWN_CUSTOMER';
    }

    /**
     * Extract customer data from CSV row
     */
    async extractCustomerFromCSV(row, profile) {
        // This would need to be implemented to extract customer data from CSV
        // For now, return mock data
        return {
            customer: null,
            billingAddress: null,
            shippingAddress: null
        };
    }

    // ===== Utility Methods =====
    normalizeProductNumber(productNumber) {
        if (!productNumber) return '';
        return productNumber.toString().trim().toUpperCase();
    }

    parsePrice(priceValue) {
        return this.csvProcessor.parsePrice(priceValue);
    }

    // Backward compatibility - delegate to country utils
    getCountryIsoCode(countryName) {
        return this.countryShipping.getCountryIsoCode(countryName);
    }

    async getCountryTaxShipping(country, profile) {
        return this.countryShipping.getCountryTaxShipping(country, profile);
    }

    // Backward compatibility - delegate to price calculator
    shouldFetchPrices(profile) {
        return this.priceCalculator.shouldFetchPrices(profile);
    }

    async getProductPricesForProfile(productNumbers, profile, customer) {
        return this.priceCalculator.getProductPricesForProfile(productNumbers, profile, customer);
    }
}

// Create and export a singleton instance
const csvImportApiClient = new CSVImportApiClient();
export default csvImportApiClient;